import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import {
  Globe,
  ArrowRight,
  CheckCircle,
  Clock,
  Warning,
  Bank,
  CurrencyDollar,
} from "@phosphor-icons/react";

// Curated set of corridors Wise sandbox accepts end-to-end. We can expand
// this list once each route is verified in live mode.
const CORRIDORS = [
  { country: "VN", country_name: "Vietnam", currency: "VND", flag: "🇻🇳", account_label: "Account number", bank_label: "Bank code (4-digit)" },
  { country: "IN", country_name: "India", currency: "INR", flag: "🇮🇳", account_label: "Account number", bank_label: "IFSC code" },
  { country: "GB", country_name: "United Kingdom", currency: "GBP", flag: "🇬🇧", account_label: "Account number (8 digits)", bank_label: "Sort code" },
  { country: "MX", country_name: "Mexico", currency: "MXN", flag: "🇲🇽", account_label: "CLABE (18 digits)", bank_label: null },
  { country: "PH", country_name: "Philippines", currency: "PHP", flag: "🇵🇭", account_label: "Account number", bank_label: null },
  { country: "BR", country_name: "Brazil", currency: "BRL", flag: "🇧🇷", account_label: "Account number", bank_label: "Bank code" },
  { country: "DE", country_name: "Germany", currency: "EUR", flag: "🇩🇪", account_label: "IBAN", bank_label: null, iban: true },
  { country: "FR", country_name: "France", currency: "EUR", flag: "🇫🇷", account_label: "IBAN", bank_label: null, iban: true },
];

const fmtMoney = (n, ccy) => new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: ccy || "USD",
  maximumFractionDigits: ccy === "VND" ? 0 : 2,
}).format(Number(n || 0));

export default function SendInternational() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [corridorIdx, setCorridorIdx] = useState(0);
  const [amount, setAmount] = useState("");
  const [holderName, setHolderName] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [bankCode, setBankCode] = useState("");
  const [iban, setIban] = useState("");
  const [reference, setReference] = useState("");
  const [receiverEmail, setReceiverEmail] = useState("");
  const [quote, setQuote] = useState(null);
  const [quoting, setQuoting] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [payout, setPayout] = useState(null);
  const [polling, setPolling] = useState(false);

  const corridor = CORRIDORS[corridorIdx];

  // Re-quote when amount or corridor changes (debounced).
  useEffect(() => {
    if (!amount || Number(amount) <= 0) { setQuote(null); return; }
    let cancelled = false;
    const t = setTimeout(async () => {
      setQuoting(true);
      try {
        const r = await api.post("/wise/quote", {
          target_currency: corridor.currency,
          source_amount: Number(amount),
        });
        if (!cancelled) setQuote(r.data);
      } catch (e) {
        if (!cancelled) {
          setQuote(null);
          toast.error(e?.response?.data?.detail || "Could not get quote");
        }
      } finally {
        if (!cancelled) setQuoting(false);
      }
    }, 500);
    return () => { cancelled = true; clearTimeout(t); };
  }, [amount, corridorIdx, corridor.currency]);

  // Poll payout status while it's non-terminal
  useEffect(() => {
    if (!payout?.id || polling) return;
    const terminal = new Set(["OUTGOING_PAYMENT_SENT", "FUNDS_CONVERTED", "INCOMING_PAYMENT_WAITING", "COMPLETED", "FAILED", "CANCELLED", "REFUNDED"]);
    if (terminal.has(payout.status)) return;
    setPolling(true);
    const interval = setInterval(async () => {
      try {
        const r = await api.get(`/wise/payouts/${payout.id}`);
        setPayout(r.data);
        if (terminal.has(r.data.status)) {
          clearInterval(interval);
          setPolling(false);
        }
      } catch {
        clearInterval(interval);
        setPolling(false);
      }
    }, 5000);
    return () => { clearInterval(interval); setPolling(false); };
  }, [payout?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const canSubmit = useMemo(() => {
    if (!quote || !holderName.trim()) return false;
    if (corridor.iban) return !!iban.trim();
    if (corridor.bank_label) return !!accountNumber.trim() && !!bankCode.trim();
    return !!accountNumber.trim();
  }, [quote, holderName, accountNumber, bankCode, iban, corridor]);

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const body = {
        quote_uuid: quote.quote_uuid,
        recipient: {
          account_holder_name: holderName.trim(),
          country_iso2: corridor.country,
          currency: corridor.currency,
          receiver_email: receiverEmail.trim() || undefined,
        },
        reference: reference.trim() || "Zcardon payout",
      };
      if (corridor.iban) body.recipient.iban = iban.trim().replace(/\s+/g, "").toUpperCase();
      else if (corridor.bank_label) {
        body.recipient.account_number = accountNumber.trim();
        body.recipient.bank_code = bankCode.trim();
      } else {
        body.recipient.account_number = accountNumber.trim();
      }
      const r = await api.post("/wise/payouts", body);
      setPayout(r.data);
      toast.success(`Payout submitted — ${fmtMoney(quote.target_amount, corridor.currency)} on its way`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Payout failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (!user) {
    navigate("/login");
    return null;
  }

  return (
    <div className="min-h-screen bg-[#FBF9F6]">
      <div className="max-w-3xl mx-auto px-6 py-12">
        <Link to="/dashboard" className="text-xs tracking-[0.3em] uppercase text-[#4A5C50] link-ul" data-testid="send-intl-back">
          ← Back to dashboard
        </Link>

        <header className="mt-6 flex items-center gap-4">
          <span className="w-12 h-12 rounded-full bg-[#184A30] text-white inline-flex items-center justify-center">
            <Globe size={22} weight="duotone" />
          </span>
          <div className="flex-1">
            <p className="text-[10px] tracking-[0.3em] uppercase text-[#4A5C50]">International payout · Sandbox</p>
            <h1 className="serif text-4xl">Send to a bank account</h1>
            <p className="text-sm text-[#4A5C50] mt-1">
              Direct deposit to your receiver's bank in 8 countries. Powered by Wise.
            </p>
          </div>
        </header>

        <div className="mt-8 rounded-2xl border border-[#E5E0D8] bg-white p-6 space-y-6">
          {/* Step 1: corridor + amount */}
          <section>
            <h2 className="serif text-lg mb-3 flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-[#184A30] text-white inline-flex items-center justify-center text-xs">1</span>
              Where to & how much
            </h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs">Recipient country</Label>
                <Select value={String(corridorIdx)} onValueChange={(v) => setCorridorIdx(Number(v))}>
                  <SelectTrigger data-testid="corridor-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CORRIDORS.map((c, i) => (
                      <SelectItem key={c.country + c.currency} value={String(i)}>
                        {c.flag} {c.country_name} ({c.currency})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">You send (USD)</Label>
                <div className="relative">
                  <CurrencyDollar size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4A5C50]" weight="duotone" />
                  <Input
                    type="number"
                    inputMode="decimal"
                    step="0.01"
                    min="1"
                    placeholder="50.00"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    className="pl-10"
                    data-testid="amount-input"
                  />
                </div>
              </div>
            </div>

            {/* Quote display */}
            {quoting && (
              <div className="mt-4 text-xs text-[#4A5C50] flex items-center gap-2">
                <Clock size={14} className="animate-pulse" /> Getting live FX rate…
              </div>
            )}
            {quote && !quoting && (
              <div className="mt-4 rounded-xl bg-[#FBF9F6] border border-[#E5E0D8] p-4" data-testid="quote-card">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-[#4A5C50]">Receiver gets</span>
                  <span className="serif text-2xl text-[#184A30]" data-testid="quote-target-amount">
                    {fmtMoney(quote.target_amount, corridor.currency)}
                  </span>
                </div>
                <div className="mt-2 grid grid-cols-3 gap-2 text-[11px] text-[#4A5C50]">
                  <div>
                    <p className="uppercase tracking-widest">Rate</p>
                    <p className="text-[#112318] font-medium">1 USD = {quote.rate.toLocaleString()} {corridor.currency}</p>
                  </div>
                  <div>
                    <p className="uppercase tracking-widest">Wise fee</p>
                    <p className="text-[#112318] font-medium">{fmtMoney(quote.fee_usd, "USD")}</p>
                  </div>
                  <div>
                    <p className="uppercase tracking-widest">Arrives</p>
                    <p className="text-[#112318] font-medium">{quote.estimated_delivery || "Soon"}</p>
                  </div>
                </div>
              </div>
            )}
          </section>

          {/* Step 2: recipient */}
          <section className="border-t border-[#E5E0D8] pt-6">
            <h2 className="serif text-lg mb-3 flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-[#184A30] text-white inline-flex items-center justify-center text-xs">2</span>
              <Bank size={16} weight="duotone" /> Receiver's bank
            </h2>
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Account holder name (exactly as on the bank account)</Label>
                <Input
                  value={holderName}
                  onChange={(e) => setHolderName(e.target.value)}
                  placeholder="Nguyễn Văn A"
                  data-testid="holder-name-input"
                />
              </div>
              {corridor.iban ? (
                <div>
                  <Label className="text-xs">IBAN</Label>
                  <Input
                    value={iban}
                    onChange={(e) => setIban(e.target.value)}
                    placeholder="DE89 3704 0044 0532 0130 00"
                    className="mono uppercase"
                    data-testid="iban-input"
                  />
                </div>
              ) : (
                <>
                  <div>
                    <Label className="text-xs">{corridor.account_label}</Label>
                    <Input
                      value={accountNumber}
                      onChange={(e) => setAccountNumber(e.target.value)}
                      placeholder=""
                      className="mono"
                      data-testid="account-number-input"
                    />
                  </div>
                  {corridor.bank_label && (
                    <div>
                      <Label className="text-xs">{corridor.bank_label}</Label>
                      <Input
                        value={bankCode}
                        onChange={(e) => setBankCode(e.target.value)}
                        className="mono uppercase"
                        data-testid="bank-code-input"
                      />
                    </div>
                  )}
                </>
              )}
              <div>
                <Label className="text-xs">Reference (optional — appears on receiver's statement)</Label>
                <Input
                  value={reference}
                  onChange={(e) => setReference(e.target.value)}
                  maxLength={35}
                  placeholder="Gift from John"
                  data-testid="reference-input"
                />
              </div>
              <div>
                <Label className="text-xs">Receiver's email (optional — we'll send them a PDF receipt when funds arrive)</Label>
                <Input
                  type="email"
                  value={receiverEmail}
                  onChange={(e) => setReceiverEmail(e.target.value)}
                  placeholder="receiver@example.com"
                  data-testid="receiver-email-input"
                />
              </div>
            </div>
          </section>

          {/* Submit */}
          <div className="border-t border-[#E5E0D8] pt-6 flex flex-wrap items-center gap-3">
            <Button
              onClick={submit}
              disabled={!canSubmit || submitting}
              className="bg-[#D96C4A] hover:bg-[#B04E2F] text-white rounded-xl"
              data-testid="submit-payout-btn"
            >
              {submitting ? "Sending…" : "Send international payout"} <ArrowRight size={16} className="ml-1" />
            </Button>
            <span className="text-[11px] text-[#4A5C50] italic">
              By submitting you agree the receiver consents to a one-time deposit to their bank account. Powered by Wise.
            </span>
          </div>

          {/* Result */}
          {payout && (
            <div className="border-t border-[#E5E0D8] pt-6" data-testid="payout-result">
              <div className={`rounded-xl border p-4 ${
                payout.status === "AWAITING_MANUAL_FUNDING"
                  ? "bg-amber-50 border-amber-200"
                  : "bg-emerald-50 border-emerald-200"
              }`}>
                <div className="flex items-start gap-3">
                  {payout.status === "AWAITING_MANUAL_FUNDING" ? (
                    <Warning size={24} weight="fill" className="text-amber-600 shrink-0" />
                  ) : (
                    <CheckCircle size={24} weight="fill" className="text-emerald-600 shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className={`font-semibold ${payout.status === "AWAITING_MANUAL_FUNDING" ? "text-amber-900" : "text-emerald-900"}`}>
                      {payout.status === "AWAITING_MANUAL_FUNDING"
                        ? "Transfer created — needs manual approval"
                        : "Payout submitted"}
                    </p>
                    <p className={`text-xs mt-0.5 mono break-all ${payout.status === "AWAITING_MANUAL_FUNDING" ? "text-amber-800" : "text-emerald-800"}`}>
                      Wise transfer ID: {payout.wise_transfer_id || payout.id}
                    </p>
                    <p className={`text-xs mt-1 ${payout.status === "AWAITING_MANUAL_FUNDING" ? "text-amber-800" : "text-emerald-800"}`}>
                      Status: <span className="font-semibold" data-testid="payout-status">{payout.status}</span>
                      {polling && " · refreshing every 5s…"}
                    </p>
                    {payout.status === "AWAITING_MANUAL_FUNDING" && (
                      <p className="text-xs text-amber-900 mt-2 leading-relaxed">
                        👉 Log into your Wise dashboard, find transfer{" "}
                        <span className="mono font-semibold">{payout.wise_transfer_id}</span>{" "}
                        and click <span className="font-semibold">"Pay now"</span> to release the funds.
                        We'll set up auto-funding via SCA in the next iteration.
                      </p>
                    )}
                    {payout.error_message && payout.status !== "AWAITING_MANUAL_FUNDING" && (
                      <p className="text-xs text-amber-700 mt-1 flex items-center gap-1">
                        <Warning size={12} /> {payout.error_message}
                      </p>
                    )}
                    <div className="mt-3 flex flex-wrap gap-2">
                      <a
                        href={`${process.env.REACT_APP_BACKEND_URL}/api/wise/payouts/${payout.id}/receipt`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 bg-[#184A30] hover:bg-[#0F3220] text-white text-xs font-semibold rounded-lg px-3 py-1.5 transition-colors"
                        data-testid="download-receipt-btn"
                      >
                        Download PDF receipt
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <p className="text-[11px] text-[#4A5C50] mt-6 italic">
          🧪 Currently in <span className="font-semibold">Sandbox mode</span> — no real money moves. Switch to live once Wise approves your business KYB.
        </p>
      </div>
    </div>
  );
}
